import React, { useState } from 'react';
import { MessageSquare, Send, Inbox, Mail, AlertCircle, User, Search, CheckCircle, Loader, XCircle } from 'lucide-react';
import { useMessages } from '../../../hooks/useDashboard';
import dashboardService from '../../../services/dashboardService';
import { useNotification } from '../../../App';

const looksLikeEmail = (value) => value.includes('@');

/**
 * UserMessages Component
 * Message inbox and sending functionality
 */
const UserMessages = () => {
    const { addNotification } = useNotification();
    const [activeTab, setActiveTab] = useState('received');
    const { messages, loading, error, sending, sendMessage, refetch } = useMessages(activeTab);
    const [showCompose, setShowCompose] = useState(false);
    const [recipientQuery, setRecipientQuery] = useState('');
    const [messageText, setMessageText] = useState('');

    const [replyingToId, setReplyingToId] = useState(null);
    const [replyText, setReplyText] = useState('');

    // 'idle' | 'loading' | 'found' | 'not_found' | 'error'
    const [lookupStatus, setLookupStatus] = useState('idle');
    const [resolvedRecipient, setResolvedRecipient] = useState(null);

    const resetRecipient = () => {
        setLookupStatus('idle');
        setResolvedRecipient(null);
    };

    const handleRecipientChange = (e) => {
        setRecipientQuery(e.target.value);
        resetRecipient();
    };

    const handleRecipientBlur = async () => {
        const query = recipientQuery.trim();
        if (!query) return;

        setLookupStatus('loading');
        try {
            const recipient = await dashboardService.findRecipient(
                looksLikeEmail(query) ? { email: query } : { phone: query }
            );
            if (recipient) {
                setResolvedRecipient(recipient);
                setLookupStatus('found');
            } else {
                setLookupStatus('not_found');
            }
        } catch (err) {
            setLookupStatus(err.status === 404 ? 'not_found' : 'error');
        }
    };

    const handleCloseCompose = () => {
        setShowCompose(false);
        setRecipientQuery('');
        setMessageText('');
        resetRecipient();
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();

        if (!resolvedRecipient || !messageText.trim()) {
            addNotification('Please find a valid recipient and write a message', 'error');
            return;
        }

        const result = await sendMessage({
            recipientId: resolvedRecipient.userId,
            message: messageText
        });

        if (result.success) {
            addNotification(`Message sent to ${resolvedRecipient.name}!`, 'success');
            handleCloseCompose();
            setActiveTab('sent');
        } else {
            addNotification(result.error || 'Failed to send message', 'error');
        }
    };

    const handleSendReply = async (message) => {
        if (!replyText.trim()) return;

        // The other party's id/name is already on the message itself - no
        // lookup needed to reply, unlike starting a new conversation.
        const recipientId = activeTab === 'received' ? message.senderId : message.recipientId;
        const recipientName = activeTab === 'received' ? message.sender?.name : message.recipient?.name;

        const result = await sendMessage({ recipientId, message: replyText });

        if (result.success) {
            addNotification(`Reply sent to ${recipientName}!`, 'success');
            setReplyingToId(null);
            setReplyText('');
        } else {
            addNotification(result.error || 'Failed to send reply', 'error');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-karibu-100">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-highland-500 mx-auto mb-4"></div>
                    <p className="text-soil-600">Loading messages...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-karibu-100">
                <div className="text-center bg-white p-8 rounded-lg shadow-md max-w-md">
                    <AlertCircle className="h-12 w-12 text-murram-500 mx-auto mb-4" />
                    <h2 className="text-xl font-semibold text-soil-900 mb-2">Error Loading Messages</h2>
                    <p className="text-soil-600 mb-4">{error}</p>
                    <button
                        onClick={() => refetch()}
                        className="px-4 py-2 bg-highland-500 text-white rounded-lg hover:bg-highland-600"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-karibu-100 pt-20 pb-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8 flex items-center justify-between">
                    <div>
                        <h1 className="font-display text-3xl font-bold text-soil-900">Messages</h1>
                        <p className="mt-2 text-soil-600">
                            Communicate with property managers and landlords
                        </p>
                    </div>
                    <button
                        onClick={() => (showCompose ? handleCloseCompose() : setShowCompose(true))}
                        className="px-4 py-2 bg-highland-500 text-white rounded-lg hover:bg-highland-600 transition-colors flex items-center"
                    >
                        <Send className="h-4 w-4 mr-2" />
                        Compose
                    </button>
                </div>

                {/* Compose Message Modal */}
                {showCompose && (
                    <div className="bg-white rounded-lg shadow-md p-6 mb-6 border border-soil-100">
                        <h3 className="text-lg font-semibold text-soil-900 mb-4">New Message</h3>
                        <form onSubmit={handleSendMessage} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-soil-600 mb-2">
                                    Recipient Email or Phone Number
                                </label>
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-soil-400" />
                                    <input
                                        type="text"
                                        value={recipientQuery}
                                        onChange={handleRecipientChange}
                                        onBlur={handleRecipientBlur}
                                        placeholder="jane@example.com or 0712345678"
                                        className="w-full pl-10 pr-3 py-2 border border-soil-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-highland-500"
                                        required
                                    />
                                </div>

                                {lookupStatus === 'loading' && (
                                    <p className="mt-1.5 flex items-center text-sm text-soil-400">
                                        <Loader className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                                        Looking up recipient...
                                    </p>
                                )}
                                {lookupStatus === 'found' && resolvedRecipient && (
                                    <p className="mt-1.5 flex items-center text-sm text-highland-600">
                                        <CheckCircle className="w-3.5 h-3.5 mr-1.5" />
                                        {resolvedRecipient.name}
                                    </p>
                                )}
                                {lookupStatus === 'not_found' && (
                                    <p className="mt-1.5 flex items-center text-sm text-murram-600">
                                        <XCircle className="w-3.5 h-3.5 mr-1.5" />
                                        No user found with that email or phone number
                                    </p>
                                )}
                                {lookupStatus === 'error' && (
                                    <p className="mt-1.5 flex items-center text-sm text-murram-600">
                                        <AlertCircle className="w-3.5 h-3.5 mr-1.5" />
                                        Couldn't look up that recipient. Please try again.
                                    </p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-soil-600 mb-2">
                                    Message
                                </label>
                                <textarea
                                    value={messageText}
                                    onChange={(e) => setMessageText(e.target.value)}
                                    placeholder="Type your message here..."
                                    rows={5}
                                    className="w-full px-4 py-2 border border-soil-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-highland-500 resize-none"
                                    required
                                />
                            </div>
                            <div className="flex items-center space-x-3">
                                <button
                                    type="submit"
                                    disabled={sending || !resolvedRecipient}
                                    className="px-6 py-2 bg-highland-500 text-white rounded-lg hover:bg-highland-600 disabled:opacity-50 transition-colors"
                                >
                                    {sending ? 'Sending...' : 'Send Message'}
                                </button>
                                <button
                                    type="button"
                                    onClick={handleCloseCompose}
                                    className="px-6 py-2 bg-soil-100 text-soil-600 rounded-lg hover:bg-soil-100/70 transition-colors"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {/* Tabs */}
                <div className="bg-white rounded-lg shadow-sm border border-soil-100 mb-6">
                    <div className="flex border-b border-soil-100">
                        <button
                            onClick={() => setActiveTab('received')}
                            className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                                activeTab === 'received'
                                    ? 'text-highland-500 border-b-2 border-highland-500'
                                    : 'text-soil-600 hover:text-soil-900'
                            }`}
                        >
                            <Inbox className="h-4 w-4 inline-block mr-2" />
                            Inbox
                        </button>
                        <button
                            onClick={() => setActiveTab('sent')}
                            className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                                activeTab === 'sent'
                                    ? 'text-highland-500 border-b-2 border-highland-500'
                                    : 'text-soil-600 hover:text-soil-900'
                            }`}
                        >
                            <Mail className="h-4 w-4 inline-block mr-2" />
                            Sent
                        </button>
                    </div>
                </div>

                {/* Messages List */}
                {messages.length > 0 ? (
                    <div className="space-y-4">
                        {messages.map((message) => (
                            <div
                                key={message.messageId}
                                className="bg-white rounded-lg shadow-sm border border-soil-100 p-6 hover:shadow-md transition-shadow"
                            >
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex items-start space-x-3">
                                        <div className="flex-shrink-0 p-2 bg-highland-50 rounded-full">
                                            <User className="h-5 w-5 text-highland-500" />
                                        </div>
                                        <div>
                                            <p className="font-semibold text-soil-900">
                                                {activeTab === 'received'
                                                    ? message.sender?.name
                                                    : message.recipient?.name}
                                            </p>
                                            <p className="text-sm text-soil-400">
                                                {activeTab === 'received'
                                                    ? message.sender?.email
                                                    : message.recipient?.email}
                                            </p>
                                        </div>
                                    </div>
                                    <p className="text-xs text-soil-400">
                                        {new Date(message.dateSent).toLocaleDateString('en-US', {
                                            month: 'short',
                                            day: 'numeric',
                                            year: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                        })}
                                    </p>
                                </div>
                                <div className="pl-11">
                                    <p className="text-soil-600 whitespace-pre-wrap">{message.message}</p>
                                </div>

                                <div className="pl-11 mt-4">
                                    {replyingToId === message.messageId ? (
                                        <div className="space-y-2">
                                            <textarea
                                                value={replyText}
                                                onChange={(e) => setReplyText(e.target.value)}
                                                placeholder={`Reply to ${activeTab === 'received' ? message.sender?.name : message.recipient?.name}...`}
                                                rows={3}
                                                autoFocus
                                                className="w-full px-3 py-2 border border-soil-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-highland-500 resize-none text-sm"
                                            />
                                            <div className="flex items-center space-x-2">
                                                <button
                                                    onClick={() => handleSendReply(message)}
                                                    disabled={sending || !replyText.trim()}
                                                    className="px-4 py-1.5 bg-highland-500 text-white text-sm rounded-lg hover:bg-highland-600 disabled:opacity-50 transition-colors"
                                                >
                                                    {sending ? 'Sending...' : 'Send Reply'}
                                                </button>
                                                <button
                                                    onClick={() => { setReplyingToId(null); setReplyText(''); }}
                                                    className="px-4 py-1.5 bg-soil-100 text-soil-600 text-sm rounded-lg hover:bg-soil-100/70 transition-colors"
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <button
                                            onClick={() => { setReplyingToId(message.messageId); setReplyText(''); }}
                                            className="flex items-center text-sm font-medium text-highland-500 hover:text-highland-600"
                                        >
                                            <Send className="h-3.5 w-3.5 mr-1.5" />
                                            Reply
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-soil-100">
                        <MessageSquare className="mx-auto h-12 w-12 text-soil-400 mb-4" />
                        <h3 className="text-lg font-medium text-soil-900 mb-2">
                            No messages {activeTab === 'received' ? 'received' : 'sent'}
                        </h3>
                        <p className="text-soil-600">
                            {activeTab === 'received'
                                ? 'Your inbox is empty'
                                : 'You haven\'t sent any messages yet'}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserMessages;
